import { addPaymentReminder, removePaymentReminder } from "api/dNet/PaymentReminder";
import { removeRatingCodesFromRisks } from "helpers/policy";
import { drawReceipt } from "api/dNet/Policies";
import SagicorPayment from "api/SagicorPayment";
import { format, isFuture } from "date-fns";
import { getCart, getBenefits } from "globals/additionalbenefits";
import {
	getActivePolicy,
	getActiveRisk,
	getPromoCodesApplied,
	getToken,
	getTransactionType,
	getUser,
	logout,
	setCurrentUser,
	login,
	getActivePolicies,
	setActivePolicies,
	getPolicyPaymentPlan,
	setMissingDocuments,
	setTransactionType,
	setPolicyHolders,
	setSelectedSession,
	getSelectedSession,
	setActivePolicy,
	getUserId,
	setProgressLine
} from "globals/auth";
import { setDefault, setRegistryId } from "globals/claims";
import { setConfirmationData } from "globals/confirmation";
import { fullLogout, clearStoreOnLogin } from "globals/misc";
import { setLoading, setLoadingMessage } from "globals/overlays";
import { persistor } from "../../globals";
import { clearPaymentForm, getPaymentFormData, getPaymentResponse, getPaymentScheduleData } from "globals/payments";
import { checkExpToken, getNewGlobalToken } from "helpers/auth";
import { AuthNetworkLayer, persistAuthTokens, PolicyHelper, PolicyNetworkLayer, ClaimsNetworkLayer } from "helpers";
import { createLog } from "helpers/log";
import {
	assignReceipt,
	extendPolicy,
	formatMoney,
	formatPolicyForRenewal,
	generateExtensionDocuments,
	getPaymentScheduleInfo,
	updateExtensions,
} from "helpers/policy";
import React, { useEffect, useRef, useState, useCallback, Suspense, lazy } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useHistory, useLocation } from "react-router";
import BillingDetails from "./components/BillingDetails";
import PaymentContent from "./components/PaymentContent";
import ReceiptErrorModal from "./components/ReceiptErrorModal";
import UpdatedModal from "components/misc/Modal-V2";
import { ERROR, INFO } from "components/misc/Modal-V2/constants";
import jwtDecoder from "jwt-decode";
import { getChangeOnPremium, getGCT } from "globals/policymanager";
import "./PaymentForm.scss";
import { PAY_BALANCE, POLICY_MANAGER, POLICY_REWRITE, QUICK_PAY } from "pages/RenewPolicy/constants";
import { clearPolicyManager, getNewPremium, getPolicyFormInfo, getPremiumSummaryDetails, setPolicyManager } from "globals/policymanager";
import changeMarketValue from "components/misc/PolicyManager/helpers";
import { nodeApi } from "api/Middleware";
import { toast } from "react-toastify";
import { isNotEmpty } from "helpers/misc";
import { FILESNEEDED } from "pages/PolicyManager/constants";
import { getBrokerPolicyCreated, getSourceType, getMotorPolicyQuestionsAndResponses, getActiveQuote } from "globals/renewal";
import * as TransactionHelper from "components/PaymentForm/helpers/index.js";
import { noticeNarratives } from "globals/globalConstants";
import { CreateDocumentAPI } from "helpers/policy/documents";
import { RenewalHelpersHook } from "./helpers/hooks/RenewalHelpersHook.jsx";
import { NoteSubmissionHook } from "./helpers/hooks/NoteSubmissionHook.jsx";
import PaymentDetailsV2 from "./components/PaymentDetailsV2";
import { v4 as uuid } from "uuid";
import branchInfo from "helpers/config";
import logger from "utils/logger"; 
import AnimateContainer from "components/AnimateContainer";
import Loader from "Loader";
const { testIds } = require("testing/testIds");

/**
 * @typedef {Object} paymentFormValues
 * @property {String=} paymentFormValues.nationalId
 * @property {String=} paymentFormValues.policyNumber
 * @property {String=} paymentFormValues.paymentAmount
 * @property {String=} paymentFormValues.paymentPurpose
 * @property {String=} paymentFormValues.paymentPurposeDetailed
 * @property {String=} paymentFormValues.currencyType
 * @property {Boolean=} paymentFormValues.fromRenewalFlow
 * @property {Object=} paymentFormValues.codes
 * @property {Object=} paymentFormValues.premiumPA_UM
 * @property {Object=} paymentFormValues.extensions
 */

/**
 * @typedef {Object} billingFormValues
 * @property {String=} billingFormValues.firstName
 * @property {String=} billingFormValues.lastName
 * @property {String=} billingFormValues.email
 * @property {String=} billingFormValues.phoneNumber
 * @property {String=} billingFormValues.billingAddress
 * @property {String=} billingFormValues.country
 * @property {String=} billingFormValues.region
 * @property {String=} billingFormValues.zip_code
 * @property {String=} billingFormValues.cardNumber
 * @property {String=} billingFormValues.cardName
 * @property {String=} billingFormValues.cardExpiryMonth
 * @property {String=} billingFormValues.cardExpiryYear
 * @property {String=} billingFormValues.cardCVC
 */

/**
 * Contains the logic for the Billing portion of the payment process
 * Requires Payment Details to be confirmed before revealing the billing portion
 * @param {Object} props
 * @param {Object} props.transaction
 * @param {String} props.flow
 * @param {React.MutableRefObject<Boolean>} props.persist
 */
function PaymentForm({ transaction, persist, flow = "" }) {
	const dispatch = useDispatch();
	const history = useHistory();
	const location = useLocation();
	const _transaction = useRef({ ...transaction });
	const cart = useSelector(getCart, shallowEqual);
	const promoCodes = useSelector(getPromoCodesApplied, shallowEqual);
	const user = useSelector(getUser, shallowEqual);
	const userId = useSelector(getUserId);
	let policy = useSelector(getActivePolicy, shallowEqual);
	const [policyState, setPolicyState] = useState(policy);
	const policies = useSelector(getActivePolicies, shallowEqual);
	const paymentResponse = useSelector(getPaymentResponse, shallowEqual);
	const paymentScheduleData = useSelector(getPaymentScheduleData, shallowEqual);
	const activeRisk = useSelector(getActiveRisk, shallowEqual);
	const policyFormInfo = useSelector(getPolicyFormInfo, shallowEqual);
	const policyPaymentPlans = useSelector(getPolicyPaymentPlan, shallowEqual);
	const premiumSummaryDetails = useSelector(getPremiumSummaryDetails, shallowEqual);
	const token = useSelector(getToken, shallowEqual);
	const newPremium = useSelector(getNewPremium, shallowEqual);
	const [paymentConfirmed, confirmPayment] = useState(transaction.paymentAmount === "0.00" ? false : true);
	const [keepBilling, persistBilling] = useState(false);
	const [attempts, setAttempts] = useState(0);
	const changeOnPremium = useSelector(getChangeOnPremium, shallowEqual);
	const gct = useSelector(getGCT, shallowEqual);
	const sourceType = useSelector(getSourceType, shallowEqual);
	const brokerPolicyCreated = useSelector(getBrokerPolicyCreated, shallowEqual);
	const { createRewrittenPolicy } = RenewalHelpersHook();
	const { handleNoteSubmission } = NoteSubmissionHook();
	const quote = useSelector(getActiveQuote);
	const savedRenewalQuestionsAndAnswers = useSelector(getMotorPolicyQuestionsAndResponses);
	const paymentformData = useSelector(getPaymentFormData);
	const addBenefits = useSelector(getBenefits);
	const [scrollPosition, setScrollPosition] = useState(1);
	const [scrollLimit, setScrollLimit] = useState(2);
	
	// const PaymentDetailsV2 = lazy(() => import("./components/PaymentDetailsV2"));
	// const BillingDetails = lazy(() => import("./components/BillingDetails"));

	logger.log("PaymentForm render: ", { policy, cart, promoCodes, addBenefits });
	/** Disable once a successful transaction is processed */
	const completedSteps = useRef({
		billing: null,
		receipt: null,
		renewal: null,
		extension: [],
		additionalPremium: null,
		paymentSchedule: null,
		receiptOnlyEmailSent: false,
	});

	logger.log("[PaymentForm] savedRenewalQuestionsAndAnswers:", savedRenewalQuestionsAndAnswers);

	if (sourceType === "Broker") completedSteps.current.renewal = brokerPolicyCreated;

	const receiptErrorModalRef = useRef();

	const toggleReceiptErrorModalState = {
		toggleModal: useCallback(() => {
			if (receiptErrorModalRef.current) {
				receiptErrorModalRef.current.toggleModal();
			}
		}, [receiptErrorModalRef]),
		openModal: useCallback(() => {
			if (receiptErrorModalRef.current) {
				receiptErrorModalRef.current.openModal();
			}
		}, [receiptErrorModalRef]),
		closeModal: useCallback(() => {
			if (receiptErrorModalRef.current) {
				receiptErrorModalRef.current.closeModal();
			}
		}, [receiptErrorModalRef]),
	};
	function addReceiptFile(transactionType, files, completedSteps) {
		if (transactionType === "manage-your-policy") {
			files.push({
				file_name: "Receipt.pdf",
				data: completedSteps?.current?.billing?.receipt,
				type: "Receipt",
			});
		} else {
			logger.log("[addReceiptFile] Pushing receipt, files before:", files.length, files);
			files.push({
				filename: "Receipt.pdf",
				fileContent: completedSteps.current.billing.receipt,
			});
			logger.log(
				"[addReceiptFile] Files after receipt:",
				files.length,
				files.map(f => f.filename)
			);
		}
	}

	function handleTransactionResponse({
		transactionResponse,
		currency,
		amount,
		paymentDetails,
		setErrorTitle,
		toast,
		sendPaymentConfirmationSms,
	}) {
		if (!transactionResponse?.success || transactionResponse?.result === "ERROR") {
			if (transactionResponse?.messageTitle) {
				setErrorTitle(transactionResponse.messageTitle);
			}
			logger.log("[handleTransactionResponse - PolMod Error] Transaction Error:", transactionResponse);
			throw new Error(`${transactionResponse?.message}.`);
		}

		const isPaymentSuccessful = transactionResponse.success;

		if (isPaymentSuccessful) {
			toast.success(`Payment of ${currency} ${amount} was successfully charged to your card.`, {
				position: "bottom-left",
				autoClose: 15000,
			});
			sendPaymentConfirmationSms({ paymentProof: transactionResponse.notificationProof });
		} else {
			toast.error(`Oh no, your payment failed. Please double check your payment information or contact your bank.`, {
				position: "bottom-left",
				autoClose: 15000,
			});
		}
	}

	/** @type {[paymentFormValues, React.Dispatch<React.SetStateAction<paymentFormValues>>]} */
	const [paymentDetails, setPaymentDetails] = useState({});

	/** @type {[billingFormValues, React.Dispatch<React.SetStateAction<billingFormValues>>]} */
	const [billingDetails, setBillingDetails] = useState({});

	const [isPaymentModalOpen, openPaymentModal] = useState(false);
	const [isErrorModalOpen, openErrorModal] = useState(false);
	const [isErrorModalOpen2, openErrorModal2] = useState(false);
	const [isValidationErrorModalOpen, openValidationErrorModal] = useState(false);
	const [error, setError] = useState("");
	const [shouldLogout, setShouldLogout] = useState(false);
	const [errorTitle, setErrorTitle] = useState(null);
	const [stepPosition, setStepPosition] = useState({
		step: 1,
		lastRequest: "back"
	});
	const transactionType = useSelector(getTransactionType, shallowEqual);
	const sessionData = useSelector(getSelectedSession, shallowEqual);
	let newlyRewrittenPolicy = null;

	useEffect(() => {
		if (location.pathname === "/payments") dispatch(setTransactionType(QUICK_PAY));
	}, [location.pathname, dispatch]);

	useEffect(() => {
		if (paymentConfirmed) persistBilling(true);
	}, [paymentConfirmed]);

	async function validateTrnPolicy(transactionType, nationalId, policyNumber) {
		dispatch(setLoading(true));
		dispatch(setLoadingMessage("Just a moment"));
		try {
			if (transactionType === QUICK_PAY && nationalId && policyNumber) {
				const response = await AuthNetworkLayer.getTrnPolicyToken(nationalId.replace(/-/g, ""), policyNumber);
				if (response.success) {
					/** @type {Object} */
					const data = jwtDecoder(response.data);

					// Clear all state AFTER successful authentication but BEFORE setting new user data
					dispatch(clearStoreOnLogin());
					// Purge persisted storage to ensure no cached data remains
					await persistor.purge();

					dispatch(setActivePolicies(data.policies));
					// set token for loggedIn user
					dispatch(setCurrentUser(data.users[0].global_name));
					dispatch(setPolicyHolders(data?.users));
					dispatch(login());
					// check for jwt
					const jwt_auth = process.env.REACT_APP_JWT_AUTHENTICATION;
					if (jwt_auth) {
						persistAuthTokens(data);
					}
					return data.token;
				}
			}
			return false;
		} catch (e) {
			return false;
		} finally {
			dispatch(setLoading(false));
		}
	}

	async function sendPaymentConfirmationSms({ paymentProof }) {
		if (!paymentProof) {
			logger.error("No payment proof; the payment confirmation SMS was not requested.");
			return;
		}
		const userPhoneNumbers = user.phone_numbers;
		if (userPhoneNumbers && userPhoneNumbers.length > 0) {
			const smsPhoneNumber = userPhoneNumbers.find(phone => phone.use_for_sms === true);
			if (smsPhoneNumber) {
				try {
					const confirmationResponse = await AuthNetworkLayer.sendPaymentConfirmation(
						// QuickPay never sets auth.userId; the current user's own id is always there.
						user.global_name_id ?? userId,
						user.national_id,
						smsPhoneNumber.phone_number,
						paymentProof
					);
					if (!confirmationResponse.success) {
						logger.error("Failed to send payment confirmation SMS:", confirmationResponse.message);
					}
				} catch (error) {
					logger.error("Error sending payment confirmation SMS:", error.message);
				}
			} else {
				logger.error("No phone number available for sending SMS.");
			}
		} else {
			logger.error("User has no phone numbers.");
		}
	}

	async function validateTrnPolicy2(transactionType, nationalId, policyNumber) {
		dispatch(setLoading(true));
		dispatch(setLoadingMessage("Just a moment"));
		try {
			if (transactionType === QUICK_PAY && nationalId && policyNumber) {
				// get token
				const response = await AuthNetworkLayer.getTrnPolicyToken(nationalId.replace(/-/g, ""), policyNumber);
				if (response.success) {
					/** @type {Object} */
					const data = jwtDecoder(response.data);

					// Clear all state AFTER successful authentication but BEFORE setting new user data
					dispatch(clearStoreOnLogin());
					// Purge persisted storage to ensure no cached data remains
					await persistor.purge();

					dispatch(setActivePolicies(data.policies));

					// set token for loggedIn user
					setPolicyState(data.policies?.find(p => String(p.policy.policy_number) === policyNumber)?.policy);
					dispatch(setActivePolicy(data.policies?.find(p => p.policy.policy_number === policyNumber)?.policy)); //dispatch(setActivePolicy(policies?.find(p => p.policy.policy_number == policyNumber)?.policy));
					dispatch(setCurrentUser(data.users[0].global_name));
					dispatch(setPolicyHolders(data?.users));
					dispatch(login());
					// check for jwt
					const jwt_auth = process.env.REACT_APP_JWT_AUTHENTICATION;
					if (jwt_auth) {
						persistAuthTokens(data);
					}
					return data.token;
				} else {
					if (response.message === "Unable to validate trn and policy number.") {
						return 1;
					}
				}
			}
			return 2;
		} catch (e) {
			return 3;
		} finally {
			dispatch(setLoading(false));
		}
	}

	async function makePayment() {  
		logger.log("[makePayment] START");
		openPaymentModal(false);
		dispatch(setLoading(true));
		let isInFlow;

		const amount =
			_transaction.current.paymentAmount === null || _transaction.current.paymentAmount === "0.00"
				? paymentDetails.paymentAmount
				: _transaction.current.paymentAmount;
		const policyNumber =
			_transaction.current.policyNumber === null ? paymentDetails.policyNumber : _transaction.current.policyNumber;
		const currency =
			_transaction.current.currency === null ? paymentDetails.currencyType : _transaction.current.currency;

		const reference =
			new Date().getDay() +
			"" +
			new Date().getUTCMonth() +
			"" +
			new Date().getFullYear() +
			new Date().getMilliseconds() +
			"-" +
			policyNumber;

		let policyManagerData = { fullname: "", email: "", registrationNumbers: [], certificates: [] };
		let faultyreference;

		// active policies should always be updated=
		//if (transactionType === QUICK_PAY) setPolicyState(olicies?.find(p => p.policy.policy_number == policyNumber)?.policy));

		const paymentData = {
			apiOperation: "PAY",
			order: {
				amount: `${amount.replace(/[, ]+/g, "").trim()}`,
				currency: `${currency}`,
				reference: `${reference}`,
			},
			customer: {
				firstName: billingDetails.firstName,
				lastName: billingDetails.lastName,
			},
			session: { id: `${paymentResponse?.session?.id}` },
			sourceOfFunds: { type: "CARD" },
			transaction: {},
				other: {
					policyPrefix: policyState?.policy_prefix,
					policyNumber: paymentDetails?.policyNumber,
					billingAddress: billingDetails?.billingAddress,
					purpose: paymentDetails?.paymentPurpose,
				},
				apiVersion: 50,
			};
		let files = [];
		let filesNeeded = [];
		let renewalResult = {};

		logger.log("[makePayment] Initial files:", files);
		logger.log("[makePayment] Initial renewalResult:", renewalResult);

		let insuranceTransactionResponse;
		let transactionStatus;
		let transactionReason;
		let isPaymentSuccessful = completedSteps.current.billing ? true : false;
		let responseAfterUpdate = null;
		let receiptResponse;

		let receipt;
		let successful = false;
		let count = 0;
		try {
			if (!completedSteps.current.billing /*&& policy.is_faulty != true*/) {
				dispatch(setLoadingMessage("Processing payment"));
				let transactionResponse = await checkExpToken({
					endpoint: `/payments`,
					payload: paymentData,
					reqType: "put",
				});

				handleTransactionResponse({
					transactionResponse,
					currency,
					amount,
					paymentDetails,
					setErrorTitle,
					toast,
					sendPaymentConfirmationSms,
				});

				completedSteps.current.billing = transactionResponse;

				if (transactionResponse?.success) {
					isPaymentSuccessful = true;
				}


				//Prevent repeat billing if subsequent actions fail
				completedSteps.current = { ...completedSteps?.current, billing: transactionResponse };
				addReceiptFile(transactionType, files, completedSteps);
			}

			//If policy number exists, transaction should not be empty
			//checks the transction type if it exist or not still run this because of quick payments
			if (transactionType !== QUICK_PAY) {
				isInFlow = true;

				//checks what Payment Type was selected
				if (
					paymentDetails.paymentPurpose.toLowerCase() === "renewal" ||
					paymentDetails?.paymentPurpose === "New Policy"
				) {
					if (policy.is_faulty) {
						newlyRewrittenPolicy = await createRewrittenPolicy();


						if (Object.keys(newlyRewrittenPolicy)?.length === 0 || newlyRewrittenPolicy?.policy_id === undefined) {
							setShouldLogout(true);
							throw new Error("We are sorry. A problem was encountered generating your policy. Please contact us at 876-978-3690");
						}

						renewalResult = {
							policy_id: newlyRewrittenPolicy?.policy_id,
							policy_number: newlyRewrittenPolicy?.policy_number,
							transaction_id: newlyRewrittenPolicy?.transaction_id,
						};
						faultyreference =
							new Date().getDay() +
							"" +
							new Date().getUTCMonth() +
							"" +
							new Date().getFullYear() +
							new Date().getMilliseconds() +
							"-" +
							renewalResult.policy_number;

						logger.log("[makePayment] renewalResult from createRewrittenPolicy:", renewalResult);

						completedSteps.current.renewal = renewalResult;
						await handleNoteSubmission(newlyRewrittenPolicy);

						logger.log(
							"[makePayment] Files before handleRewrittenPolicyTransaction:",
							files.map(f => f.filename)
						);

						// dispatch(setLoadingMessage("Processing payment"));
						// const receiptData = {
						// 	apiOperation: "PAY",
						// 	order: {
						// 		amount: `${amount.replace(/[, ]+/g, "").trim()}`,
						// 		currency: `${currency}`,
						// 		reference: `${faultyreference}`,
						// 	},
						// 	customer: {
						// 		firstName: billingDetails.firstName,
						// 		lastName: billingDetails.lastName,
						// 	},
						// 	session: { id: `${paymentResponse?.session?.id}` },
						// 	sourceOfFunds: { type: "CARD" },
						// 	transaction: {},
						// 	other: {
						// 		policyPrefix: newlyRewrittenPolicy.policy_prefix,
						// 		policyNumber: newlyRewrittenPolicy.policy_number,
						// 		billingAddress: billingDetails.billingAddress,
						// 		purpose: "Policy Rewrite", // TODO: This is the purpose for the new policy
						// 	},
						// };

						// let transactionResponse = await checkExpToken({
						// 	endpoint: `/payments`,
						// 	payload: receiptData,
						// 	reqType: "put",
						// });

						// try {
						// 	handleTransactionResponse({
						// 		transactionResponse,
						// 		currency,
						// 		amount,
						// 		paymentDetails,
						// 		setErrorTitle,
						// 		toast,
						// 		sendPaymentConfirmationSms,
						// 	});
						// } catch (error) {
						// 	logger.error("Payment error:", error.message);
						// }
						//completedSteps.current = { ...completedSteps?.current, billing: transactionResponse };
						addReceiptFile(transactionType, files, completedSteps);
						const result = await TransactionHelper.handleRewrittenPolicyTransaction({
							PolicyHelper,
							policy: newlyRewrittenPolicy,
							user,
							completedSteps,
							paymentDetails,
							_transaction,
							setLoadingMessage,
							dispatch,
							files,
							policyNumber: newlyRewrittenPolicy?.policy_number,
							getPaymentScheduleInfo,
							addPaymentReminder,
							renewalResult,
							responseAfterUpdate,
							quote,
							paymentformData,
							PolicyNetworkLayer,
							oldPolicy: { ...policy },
							ClaimsNetworkLayer
						});

						logger.log("[makePayment] handleRewrittenPolicyTransaction result:", result);
						logger.log(
							"[makePayment] Files after:",
							files.map(f => f.filename)
						);
					} else {
						const handleRenewalTransactionArgumentObject = {
							user,
							policy: policyState,
							formatPolicyForRenewal,
							updateExtensions,
							PolicyHelper,
							_transaction,
							paymentDetails,
							completedSteps,
							dispatch,
							setLoadingMessage,
							addBenefits,
							cart,
							promoCodes,

							policyNumber,
							renewalResult,
							getPaymentScheduleInfo,
							addPaymentReminder,

							files,
							responseAfterUpdate,
							activeRisk,
							/* assignReceipt,
							drawReceipt,
							createLog,
							toggleReceiptErrorModalState,
							checkExpToken,
							transactionType,
							reference,
							billingDetails,
							error,
							currency,
							tmpToken,
							amount,
							toast,
							 */
						};
						
						await handleNoteSubmission(policyState);
						insuranceTransactionResponse = await TransactionHelper.handleRenewalTransaction(
							handleRenewalTransactionArgumentObject
						);
					}
				} else if (paymentDetails.paymentPurpose.toLowerCase() === "pay balance") {
					const handlePayBalanceTransactionArgumentObject = {
						dispatch,
						setLoadingMessage,
						completedSteps,
						policy: policyState,
						user,
						policyNumber,

						getPaymentScheduleInfo,
						_transaction,
						generateExtensionDocuments,
						extendPolicy,
						paymentScheduleData,
						isFuture,
						addPaymentReminder,
						removePaymentReminder,

						files,
						responseAfterUpdate,
						/* assignReceipt,
						paymentDetails,
						drawReceipt,
						toggleReceiptErrorModalState,
						createLog,
						transactionType,
						reference,
						error,
						amount,
						billingDetails,
						currency,
						tmpToken,
						checkExpToken,
						toast,
						 */
					};
					insuranceTransactionResponse = await TransactionHelper.handlePayBalanceTransaction(
						handlePayBalanceTransactionArgumentObject
					);
				} else if (paymentDetails.paymentPurpose.toLowerCase() === "additional premium") {
					const handleAdditionalPremiumTransactionArgumentObject = {
						policyFormInfo,
						activeRisk,
						changeMarketValue,

						policy: policyState,
						changeOnPremium,
						gct,

						policyPaymentPlans,
						setError,
						openErrorModal2,
						user,
						PolicyNetworkLayer,
						completedSteps,
						/* assignReceipt,
						paymentDetails,
						,
						drawReceipt,
						toggleReceiptErrorModalState,
						createLog,
						transactionType,
						reference,
						error,
						checkExpToken,
						amount,
						billingDetails,
						policyNumber,
						currency,
						tmpToken, */
						dispatch,
						setLoadingMessage,
						toast,
						filesNeeded,
						FILESNEEDED,
						formatMoney,
						newPremium,
						policyManagerData,
						isNotEmpty,
						nodeApi,
						files,
					};
					insuranceTransactionResponse = await TransactionHelper.handleAdditionalPremiumTransaction(
						handleAdditionalPremiumTransactionArgumentObject
					);
				}
				else if (paymentDetails.paymentPurpose.toLowerCase() === "adding a vehicle") {
					const handleAdditionalVehicleArgumentObject = {
						policyFormInfo,
						activeRisk,
						policy: policyState,
						policyPaymentPlans,
						setError,
						openErrorModal2,
						user,
						PolicyNetworkLayer,
						completedSteps,
						dispatch,
						setLoadingMessage,
						toast,
						filesNeeded,
						FILESNEEDED,
						formatMoney,
						newPremium,
						policyManagerData,
						isNotEmpty,
						nodeApi,
						files,
						changeOnPremium,
						gct,
					};
					insuranceTransactionResponse = await TransactionHelper.handleAdditionalVehicleTransaction(
						handleAdditionalVehicleArgumentObject
					);
				}

				else if (paymentDetails.paymentPurpose.toLowerCase() === "changing a vehicle") {
					const handleChangeVehicleArgumentObject = {
						policyFormInfo,
						activeRisk,
						policy: policyState,
						oldRisk: premiumSummaryDetails?.oldRisk || null,
						policyPaymentPlans,
						setError,
						openErrorModal2,
						user,
						PolicyNetworkLayer,
						completedSteps,
						dispatch,
						setLoadingMessage,
						filesNeeded,
						FILESNEEDED,
						formatMoney,
						newPremium,
						policyManagerData,
						isNotEmpty,
						nodeApi,
						files,
						changeOnPremium,
						gct,
						transactionPremium: premiumSummaryDetails?.transactionPremium || 0,
					};
					insuranceTransactionResponse = await TransactionHelper.handleChangeVehicleTransaction(
						handleChangeVehicleArgumentObject
					);
				}
			}

			const getHomePolicyDocuments = async () => {
				const userDefinedFormIds = {
					826: "Average Clause",

					638: "Fungus Mildew & Mold Exclusion",

					801: "Mortgagee Clause - Non Motor",
				};

				// Use the newly created policy for document generation
				const policyForDocuments = newlyRewrittenPolicy?.policy_id
					? newlyRewrittenPolicy
					: sourceType === "Broker" && brokerPolicyCreated?.policy_id
						? brokerPolicyCreated
						: policyState;

				let riskId;
				const keys = Object.keys(userDefinedFormIds);

				for (const key of keys) {
					riskId = null;

					if (key === "801") {
						for (let i = 0; i < policyForDocuments?.risks?.length; i++) {
							if (!policyForDocuments?.risks[i]?.mortgagees?.length) continue;

							riskId = policyForDocuments?.risks[i].risk_id;
						}
					}

					const createDocumentAPIPayload = {
						formId: parseInt(key),
						shouldGeneratePdf: true,
						claimId: null,
						policyId: policyForDocuments.policy_id,
						riskItemId: riskId,
						transactionId: completedSteps.current.renewal?.transaction_id,
					};

					try {
						const homePolicyFileResponse = await CreateDocumentAPI(createDocumentAPIPayload);

						if (homePolicyFileResponse?.success) {
							const file = homePolicyFileResponse?.file;
							const fileName = file?.description;
							files.push({ filename: fileName + ".pdf", fileContent: file?.data });
						}
					} catch (e) {
						logger.log("Error: ", e);
					}
				}
			};

			if (
				paymentDetails.paymentPurpose.toLowerCase() === "renewal" &&
				completedSteps.current.renewal &&
				policy.department_class.toLowerCase() === "fire"
			) {
				await getHomePolicyDocuments();
			}

			if ((paymentDetails.paymentPurpose.toLowerCase() === "adding a vehicle" || paymentDetails.paymentPurpose.toLowerCase() === "changing a vehicle")
				&& completedSteps.current.additionalPremium) {
				//Create Registry for registration documents

				const received_from = "";
				const note = "";
				const currentPolicyNumber = policy?.policy_number;
				const description = "Make Change To Policy";
				const registryType = "Endorsement";
				const assigned_to_id = "AQIAxlPQmorNjPZVElBfSky1WL5CRgm2Sg12KgMxrd19HEkzpilYvb/uh4pF92Zh/oxhAv9YOpv5J+z/HhAnz+x8EQ=="; // TODO: more efficent ways to set tehe assigned to user oid for 'Online Underwriting Docs'


				let registryResult = await PolicyNetworkLayer?.sendRegistryEmail(

					branchInfo?.branch_name,
					policy,
					user,
					received_from,
					{},
					note,
					"",
					currentPolicyNumber,
					description,
					registryType,
					"Policy",
					"Certificate of Registration",
					"Underwriting",
					assigned_to_id
				);

				if (registryResult.success)
					dispatch(setRegistryId(registryResult.registry_id));
			}
		} catch (e) {
			logger.error("[makePayment] ERROR", e);
			setError(`${e.message}`);
			//renewalResult.success = false;
			//renewalResult.error_message = `An error occurred. ${e.message}`;
			setAttempts(attempts + 1);
			dispatch(setLoading(false));
			openErrorModal(true);
			return;
		} finally {
			if (completedSteps.current.billing && !completedSteps.current.receipt) {
				try {
					// Use the newly created policy for receipt generation
					// 1. Quote Flow: uses brokerPolicyCreated
					// 2. Faulty Policy Flow: uses newlyRewrittenPolicy
					// 3. Normal Flow: uses policyState
					const policyForReceipt = newlyRewrittenPolicy?.policy_id
						? newlyRewrittenPolicy
						: sourceType === "Broker" && brokerPolicyCreated?.policy_id
							? brokerPolicyCreated
							: policyState;

					receipt = assignReceipt({
						policy: policyForReceipt,
						global_name_id: user.userId,
						total: paymentDetails.paymentAmount,
						premiumPA_UM: paymentDetails.premiumPA_UM,
					});

					if (!completedSteps.current.receipt) {
						while (count < 2 && !receiptResponse) {
							receiptResponse = await drawReceipt(receipt);
							if (receiptResponse && Object.keys(receiptResponse).length === 0) {
								receiptResponse = null;
							}
							count++;
						}
						if (!receiptResponse || !receiptResponse?.success) {
							if (!receiptResponse) {
								receiptResponse = { success: false, error_message: "Request Timeout or No Response" };
							}
							completedSteps.current.receipt = receiptResponse;
						} else {
							completedSteps.current.receipt = receiptResponse;
							successful = true;
						}
					}
				} catch (e) {
					//	toggleReceiptErrorModalState.openModal();
				} finally {

					successful = completedSteps.current.receipt ? true : false;


					let confirmationResult;
					if (transactionType === QUICK_PAY || !successful)
						try {
							confirmationResult = await checkExpToken({
								endpoint: "/email/quickpay",
								// AWE-5271: policy, name, amount, currency, receipt and type come from the proof.
								payload: {
									payment_proof: completedSteps.current.billing?.notificationProof,
									email: billingDetails.email,
									phone: billingDetails.phoneNumber,
									successful: successful,
									transaction_type: transactionType,
								},
							});
						} catch (e) {
							confirmationResult.message = e.message;
						} finally {
							createLog({
								trn: user?.national_id,
								type: `${transactionType}-Paymentform-Confirmation`,
								data: {
									confirmationResult,
								},
								errors: { message: error || !confirmationResult?.success ? confirmationResult?.message : "" },
							});
						}
					createLog({
						trn: user?.national_id,
						type: `${transactionType}-Paymentform-DrawReceipt`,
						data: {
							receiptNum: reference,
							receipt: receipt,
							filesGenerated: files?.map(f => f.filename),
							receiptResponse,
							Count: count,
						},
						errors: { message: error || !receiptResponse?.success ? receiptResponse?.error_message : "" },
					});
				}
			}
			if (isPaymentSuccessful) {
				const source = transactionType === QUICK_PAY ? "Quick Pay" : "Web";
				const sessionType =
					transactionType === QUICK_PAY
						? paymentDetails.paymentPurpose.toLowerCase()
						: paymentDetails.paymentPurpose.toLowerCase() === "additional premium" && paymentDetails.newMarketValue
							? paymentDetails.paymentPurpose.toLowerCase() : policy?.is_faulty
								? POLICY_REWRITE
								: transactionType;

				// Use the newly created policy for session data
				const policyForSession = newlyRewrittenPolicy?.policy_id
					? newlyRewrittenPolicy
					: sourceType === "Broker" && brokerPolicyCreated?.policy_id
						? brokerPolicyCreated
						: policyState;

				//save session payloads
				const saveSessionPayload = {
					user,
					policy: policyForSession,
					paymentForm: {
						...paymentData,
						paymentAmount: amount.replace(/[, ]+/g, "").trim(),
						receipt_no: completedSteps?.current?.receipt?.receipt_number,
					},
					session_type: sessionType,
					receipt_method: "EMAIL",
					was_broker_policy: sourceType === "Broker",
					branch: { title: "AGIC Online" },
					receiptFailed: !completedSteps?.current?.receipt,
					source: source,
					receipt_reason: completedSteps?.current?.receipt?.success ? null : completedSteps?.current?.receipt?.error_message,
				};

				//Paymentdetails payload
				const savePaymentDetailsPayload = {
					session_id: "",
					receipt_amount: amount.replace(/[, ]+/g, "").trim(),
					paymentmethod: "Credit card",
					currency: "JMD",
					receipt_no: completedSteps?.current?.receipt?.receipt_number,
					rate_of_exchange: /* p.currency === "JMD" ? */ "1",
				};

				let payBalanceResponse = {};

				if (transactionType === PAY_BALANCE && insuranceTransactionResponse)
					payBalanceResponse = insuranceTransactionResponse?.findLast(term => term);

				if (!payBalanceResponse) payBalanceResponse = completedSteps.current.extension?.findLast(term => term);

				transactionStatus =
					transactionType === QUICK_PAY
						? "Unknown"
						: payBalanceResponse?.success /*<- Paybalance check*/ || insuranceTransactionResponse?.success
							? "SUCCESS" : sessionType === POLICY_REWRITE ? Object.keys(newlyRewrittenPolicy)?.length === 0 || newlyRewrittenPolicy?.policy_id === undefined ? "FAILED" : "SUCCESS"
								: "FAILED";

				transactionReason =
					transactionType === QUICK_PAY
						? "Awaiting action from Telebranch" :
						sessionType === POLICY_REWRITE ?
							Object.keys(newlyRewrittenPolicy)?.length === 0 || newlyRewrittenPolicy?.policy_id === undefined ?
								"Transaction was unsuccessful" : "Transaction was successful"

							: payBalanceResponse?.success || insuranceTransactionResponse?.success
								? "Transaction was successful"
								: payBalanceResponse?.error_message /*<- Paybalance check*/ ??
								completedSteps.current?.renewal?.error_message ??
								completedSteps.current.additionalPremium;

				const sessionUpdatePayload = {
					session_id: "",
					fields_to_update: {
						receipt_no: completedSteps?.current?.receipt?.receipt_number,
						status: "COMPLETED",
						cashier_user_id: "MW",
						receipt_amount: amount.replace(/[, ]+/g, "").trim(),
						payment_method: "Credit Card",
						payment_date: format(new Date(), "yyyy-MM-dd HH:mm:ss"),
						transaction_status: transactionStatus,
						transaction_reason: transactionReason,
						activePolicy: policyForSession,
						receipt_reason: completedSteps?.current?.receipt?.success ? null : completedSteps?.current?.receipt?.error_message,
					},
				};

				const payload = {
					saveSessionPayload,
					savePaymentDetailsPayload,
					sessionUpdatePayload,
					sessionTracking: {
						dispatch,
						sessionData,
						setSelectedSession,
					},
				}; 

				try {
					await PolicyNetworkLayer.savePaymentActivity(payload);
				} catch (e) {
					logger.error("[PaymentForm] savePaymentActivity error:", e);
				}
			} else {
				// savePaymentActivity was never called because the payment wasn't successful
				logger.warn("[PaymentForm] Payment was not successful; savePaymentActivity was not invoked.");
				createLog({
					trn: user?.national_id,
					type: `${transactionType}-Paymentform-SavePaymentActivityNotCalled`,
					data: { isPaymentSuccessful },
					errors: {},
				});
			}

			createLog({
				trn: user?.national_id,
				type: `${transactionType}-PaymentForm`,
				data: { receiptNum: reference, filesGenerated: files.map(f => f.filename), renewalResult },
				errors: { message: error || !renewalResult?.success ? renewalResult?.error_message : "" },
			});

			logger.log("[makePayment] END");
		}

		const paymentPurposeLower = paymentDetails?.paymentPurpose?.toLowerCase?.() || "";
		const isVehiclePolicyManagerFlow =
			transactionType === POLICY_MANAGER &&
			(
				paymentPurposeLower === "adding a vehicle" ||
				paymentPurposeLower === "changing a vehicle"
			);
		const shouldSendReceiptOnlyEmail =
			transactionType === POLICY_MANAGER &&
			completedSteps.current.receipt?.success &&
			!completedSteps.current.receiptOnlyEmailSent &&
			(
				paymentPurposeLower === "additional premium" ||
				isVehiclePolicyManagerFlow
			) &&
			insuranceTransactionResponse?.emailPayload;

		const sendReceiptOnlyEmail = async () => {
			if (!shouldSendReceiptOnlyEmail) return;

			const { customerInfo, vehiclesInfo, driversInfo } = insuranceTransactionResponse.emailPayload;

			try {
				const timeoutPromise = new Promise((_, reject) =>
					setTimeout(() => reject(new Error("Email timeout")), 15000)
				);

				const receiptEmailResult = await Promise.race([
					checkExpToken({
						endpoint: "/email/policyManager",
						payload: {
							customerInfo,
							vehiclesInfo,
							driversInfo,
							variant: "receiptOnly",
						},
					}),
					timeoutPromise,
				]);

				if (receiptEmailResult?.success) {
					completedSteps.current.receiptOnlyEmailSent = true;
					if (isVehiclePolicyManagerFlow) {
						toast.success("An email has been sent with an outline of all your changes.");
					}
				} else {
					logger.warn("Receipt email send failed:", receiptEmailResult?.error_message);
				}
			} catch (emailError) {
				logger.error("Receipt email send error:", emailError.message);
			}
		};

		// Validate policy modification transactions after payment and receipt are complete
		// but before proceeding to email/confirmation flow
		if (isPaymentSuccessful && completedSteps.current.receipt) {
			// Check if policy modification failed for vehicle-related transactions
			if (
				(paymentPurposeLower === "additional premium" ||
				paymentPurposeLower === "adding a vehicle" ||
				paymentPurposeLower === "changing a vehicle") && transactionType !== QUICK_PAY
			) {
				if (!insuranceTransactionResponse?.success) {
					const errorMsg = insuranceTransactionResponse?.message ||
						insuranceTransactionResponse?.error_message ||
						"Policy modification failed";
					logger.error("[makePayment] Policy modification failed after successful payment:", errorMsg);
					setError(errorMsg);
					dispatch(setLoading(false));
					openErrorModal2(true);
					return;
				}
			}
		}

		if (transactionType === POLICY_MANAGER) {
			dispatch(clearPolicyManager());
			dispatch(setPolicyManager(policyManagerData));

			dispatch(
				setConfirmationData({
					receiptNum: reference,
					responseAfterUpdate,
					billingDetails,
					paymentDetails,
					isInFlow: isInFlow,
					files,
					policyManagerEmailPayload: insuranceTransactionResponse?.emailPayload || null,
				})
			);

			// Redirect to UploadSupportingDocuments for vehicle transactions if there are no errors
			if (
				!error &&
				!isErrorModalOpen2 &&
				isVehiclePolicyManagerFlow
			) {
				history.replace("/manage-your-policy/upload-supporting-documents", { from: 'payments' });
			} else {
				history.replace("success-page");
			}

			dispatch(setLoading(false));
			void sendReceiptOnlyEmail();
		} else {
			// not a real logout - block from reentering the flow
			dispatch(fullLogout());
			// reset the wasEmailSent variable located in claimsStore ( consider relocating)
			dispatch(setDefault());

			let missingDocs = policyState?.missing_documents
				? policyState?.missing_documents.filter(narrative => {
					return !noticeNarratives.includes(narrative.code);
				})
				: [];

			dispatch(setMissingDocuments(missingDocs));
			dispatch(
				setConfirmationData({
					receiptNum: policy?.is_faulty ? faultyreference : reference,
					responseAfterUpdate,
					billingDetails,
					paymentDetails: {
						...paymentDetails,
						rewrite: policy?.is_faulty,
						rewrittenPolicyNumber: newlyRewrittenPolicy?.policy_number,
						isHomePolicy: policyState?.department_class === "Fire",
					},
					isInFlow: isInFlow,
					files,
					policyManagerEmailPayload: null,
				})
			); 
		
			if (completedSteps.current.receipt) history.push("/confirmation");
			dispatch(setLoading(false));
		}
	}

	/**
	 * Function to mask the middle 8 digits of a card number
	 * @param {String} cardnumber
	 */
	function maskCreditCard(cardnumber) {
		const first4 = cardnumber.substring(0, 4);
		const last5 = cardnumber.substring(cardnumber.length - 5);
		const mask = cardnumber.substring(4, cardnumber.length - 5).replace(/\d/g, "x");
		return first4 + mask + last5;
	}

	/**
	 * Hide first 8, mask next 4, show last 4 (i.e., mask first 12 digits).
	 */
	function maskCreditCardv2(cardnumber) {
		if (cardnumber == null) return "";
		const digits = String(cardnumber).replace(/\D/g, "");
		if (digits.length <= 4) return "x".repeat(digits.length);

		const last4 = digits.slice(-4);
		const maskedPrefix = "*".repeat(digits.length - 4);

		return maskedPrefix + last4;
	}

	/**
	 * Shows a new payment error
	 * @param {String} message
	 */
	function showError(message) {
		setError(message);
		openErrorModal(true);
	}

	/**
	 * Shows a new TRN/Policy No. validation error
	 * @param {String} message
	 */
	function showValidationError(message) {
		setError(message);
		openValidationErrorModal(true);
	}

	/** Simple conditional check to redirect to home if session is to be ended */
	function handleModalClose() { }

	const closeModal = () => {
		openErrorModal(false);
		setErrorTitle(null);
	};

	const closeValidationModal = () => {
		openValidationErrorModal(false);
	};

	function updateStep(positionIncrement, lastRequest){ 
		setStepPosition((prev) => {
			return {
				step: positionIncrement,
				lastRequest: lastRequest
			};
		})
	}

	return (
		<div className="payment-form-container">
				{flow === "make-a-payment" && <div 
					className="main-cont-header"
				>
					<div>
						<h1 style={{ fontSize: "28px", fontWeight: "600", color: "#333333", marginBottom: "8px"}}>Make a Payment</h1>
						<p style={{ fontSize: "16px", fontWeight: "600", color: "#666666", margin: 0}}>Please enter your details to make a payment</p>
					</div>
				</div>}
				{((stepPosition.step == 1 && transactionType === QUICK_PAY ) || (transactionType != QUICK_PAY && transactionType != null)) && 
				<div
					className="wrap-form"
				>
					<>
						<div className={`${transactionType === QUICK_PAY ? (stepPosition.lastRequest === "back" ? "custom-fade-in-right" : "custom-fade-in-left") : "" }`}>
							<PaymentDetailsV2
								transaction={_transaction.current}
								confirmPayment={confirmPayment}
								paymentConfirmed={paymentConfirmed}
								setPaymentDetails={setPaymentDetails}
								validateTrnPolicy2={validateTrnPolicy2}
								showValidationError={showValidationError}
								persist={persist}
								updateStep={updateStep}
							/>
						</div>
					</>
				</div>}
			{(((stepPosition.step == 2 || stepPosition.step == 3 || stepPosition.step == 4) && (transactionType === QUICK_PAY )) || (transactionType != QUICK_PAY && transactionType != null)) &&
				<>
				<BillingDetails
					setBillingDetails={setBillingDetails}
					showPaymentModal={openPaymentModal}
					showError={showError}
					transaction={_transaction.current}
					updateStep={updateStep}
					stepPosition={stepPosition}
					confirmPayment={confirmPayment}
				/>
				</>
				
			} 
			<UpdatedModal
				modalType={INFO}
				showModal={isPaymentModalOpen}
				closeModal={() => {
					openPaymentModal(false);
				}}
				title={"Review Payment"}
				subTitle={"Please verify the details below before proceeding."}
				containerTestId={testIds.renewal.cardPayment.modal.confirmationContainer}
				primaryLabel={"Accept & Pay"}
				primaryTestId={testIds.renewal.cardPayment.modal.acceptButton}
				primaryCallback={() => {
					// Instead of calling makePayment directly, trigger 3DS flow first
					makePayment();
					dispatch(setProgressLine({
						enabled: true,
						percentage: 80,
					}));
				}}
				secondaryLabel="Cancel"
				secondaryTestId={testIds.renewal.cardPayment.modal.cancelButton}
				secondaryCallback={() => {
					openPaymentModal(false);
				}}
			>
				{
					paymentDetails.policyNumber &&
					paymentResponse.sourceOfFunds && (
						<PaymentContent
							paymentDetails={paymentDetails}
							paymentResponse={paymentResponse}
							maskCreditCard={maskCreditCardv2}
							billingDetails={billingDetails}
							reWrite={policy?.is_faulty}
						/>
					)
				}
			</UpdatedModal>

			<UpdatedModal
				modalType={ERROR}
				showModal={isErrorModalOpen}
				closeModal={() => closeModal()}
				title={errorTitle ?? "An Error Occurred"}
				primaryLabel={shouldLogout ? "Logout" : "OK"}
				primaryCallback={() => {
					if (shouldLogout) {
						dispatch(fullLogout());
						history.push("/payments-landing");
					} else {
						closeModal()
					}
				}}
				secondaryLabel="Contact Support"
				secondaryCallback={() => {
					if (window.$crisp) {
						window.$crisp.push(["do", "chat:open"]);
					}
				}}
			>
				{error}
			</UpdatedModal>

			<UpdatedModal
				modalType={ERROR}
				showModal={isValidationErrorModalOpen}
				closeModal={() => closeValidationModal}
				title={"Unable to Verify"}
				primaryLabel="OK"
				primaryCallback={closeValidationModal}
				secondaryLabel="Contact Support"
				secondaryCallback={() => {
					if (window.$crisp) {
						window.$crisp.push(["do", "chat:open"]);
					}
				}}
			>
				{error}
			</UpdatedModal>

			<UpdatedModal
				modalType={ERROR}
				showModal={isErrorModalOpen2}
				title={"Failed to Update Policy"}
				primaryLabel="Exit"
				primaryCallback={() => {
					dispatch(fullLogout());
					history.push("/payments-landing");
				}}
				secondaryLabel="Contact Support"
				secondaryCallback={() => {
					if (window.$crisp) {
						window.$crisp.push(["do", "chat:open"]);
					}
				}}
				disableSecondary={shouldLogout}
				disablePrimary={false}
			>
				<p>
					Your policy changes failed to update. But don’t worry, we can help. Contact us via live chat and a representative will happy to assist you with these changes.
				</p>
			</UpdatedModal>

			<ReceiptErrorModal
				ref={receiptErrorModalRef}
				policyNumber={
					(_transaction.current.policyNumber === null || _transaction.current.policyNumber === undefined) ? paymentDetails.policyNumber : _transaction.current.policyNumber
				}
				amount={
					(_transaction.current.paymentAmount === null || _transaction.current.paymentAmount === undefined) ? paymentDetails.paymentAmount : _transaction.current.paymentAmount
				}
				currency={(_transaction.current.currency === null || _transaction.current.currency === undefined) ? paymentDetails.currencyType : _transaction.current.currency}
				customer={{
					firstName: billingDetails.firstName,
					lastName: billingDetails.lastName,
					address: billingDetails.billingAddress,
				}}
				paymentType={paymentDetails.paymentPurpose}
			/>

		</div>
	);
}

export default PaymentForm;
